import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import {
  getMasterDataChangeConfiguration,
  ImmomioIconName,
  MasterdataChangeRequestType,
  MasterdataChangeType,
  PaymentInformationBaseComponent,
} from '@resident-nx/shared';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import { CardWebComponent } from '@resident-nx/web/ui';
import { ImmomioAnchorComponent, ImmomioButtonComponent } from '@immomio/ui/button';
import { ROUTE } from '../../../models';
import { RouterWebService, SideSheetService, TicketingGuardService } from '../../../services';
import { IconWebComponent } from '@resident-nx/web/ui';
import { A11yPressDirective } from '../../../directives/a11y/a11y-press.directive';
import { UserProfileEditByTicketComponent } from '../user-profile/user-profile-edit-by-ticket/user-profile-edit-by-ticket.component';
import { Store } from '@ngrx/store';
import { PaymentInformationEditComponent } from './payment-information-edit/payment-information-edit.component';
import { map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { ModalService } from '../modal/modal.service';

@Component({
  standalone: true,
  selector: 'rs-web-payment-information',
  templateUrl: './payment-information.component.html',
  styleUrls: ['./payment-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    AsyncPipe,
    CardWebComponent,
    DatePipe,
    ImmomioAnchorComponent,
    ImmomioButtonComponent,
    IconWebComponent,
    A11yPressDirective,
  ],
})
export class PaymentInformationComponent extends PaymentInformationBaseComponent {
  protected readonly ImmomioIconName = ImmomioIconName;
  #routerService = inject(RouterWebService);
  #ticketingGuard = inject(TicketingGuardService);
  #sideSheetService = inject(SideSheetService);
  #modalService = inject(ModalService);
  #store = inject(Store);
  public expandElList = signal<number[]>([]);
  private paymentConfig = toSignal(
    this.#store
      .select(getMasterDataChangeConfiguration)
      .pipe(map(config => config?.find(c => c.changeType === MasterdataChangeType.PAYMENT)))
  );

  public editPaymentData() {
    const hasPaymentConfig = !!this.paymentConfig();

    if (hasPaymentConfig) {
      if (this.paymentConfig().requestType === MasterdataChangeRequestType.EXTERNAL) {
        const day = new Date().getDate();
        const { restrictionPeriodStartDay, restrictionPeriodEndDay } =
          this.paymentConfig().externalPaymentData;
        if (restrictionPeriodStartDay <= day && day <= restrictionPeriodEndDay) {
          this.#modalService.openConfirmation({
            data: {
              message: 'edit_payment_not_allowed_restriction_period',
              acknowledge: true,
              okButtonMessage: 'general.ok',
              messageValue: {
                startDay: restrictionPeriodStartDay,
                endDay: restrictionPeriodEndDay,
              },
            },
            centered: true,
          });
        } else {
          this.#sideSheetService.open(PaymentInformationEditComponent, {
            data: { notice: this.paymentConfig().externalPaymentData?.notice },
          });
        }
      } else {
        this.#ticketingGuard.setPath(ROUTE.REQUESTS);
        void this.#routerService.navigate([ROUTE.TICKETS, ROUTE.REQUESTS]);
        const { ticketCategoryId, changeType } = this.paymentConfig();
        this.#sideSheetService.open(UserProfileEditByTicketComponent, {
          data: { ticketCategoryId, changeType },
        });
      }
    } else {
      this.#ticketingGuard.setPath(ROUTE.REQUESTS);
      void this.#routerService.navigate([ROUTE.TICKETS, ROUTE.REQUESTS]);
    }
  }

  toggleEl(index: number) {
    if (this.expandElList().includes(index)) {
      this.expandElList.set(this.expandElList().filter(item => item !== index));
    } else {
      this.expandElList.set([...this.expandElList(), index]);
    }
  }
}
